import { DateTime } from 'luxon'

const CURRENT_YEAR = DateTime.now().year

const DATE_FORMAT = 'MMMM d, yyyy'
const MOBILE_DATE_FORMAT = 'EEE, MMMM d'

function compareStartDates(a, b, direction) {
  const aTime = DateTime.fromISO(a.lesson.startDateTime).toMillis()
  const bTime = DateTime.fromISO(b.lesson.startDateTime).toMillis()

  if (aTime === bTime) return 0

  return direction === 'asc' ? aTime - bTime : bTime - aTime
}

function descendingDates(a, b) {
  return compareStartDates(a, b, 'desc')
}

function ascendingDates(a, b) {
  return compareStartDates(a, b, 'asc')
}

function isPastSession(session) {
  return DateTime.fromISO(session.lesson.startDateTime) < DateTime.now()
}

function isUpcomingSession(session) {
  return DateTime.fromISO(session.lesson.startDateTime) >= DateTime.now()
}

export function parsePastSessions(sessions) {
  return sessions.filter(isPastSession).sort(descendingDates)
}

export function parseUpcomingSessions(sessions) {
  return sessions.filter(isUpcomingSession).sort(ascendingDates)
}

// Pass in result of parsePastSessions which already filters the array.
export function yearsSelect(sessions) {
  const uniq = new Set(
    sessions.map(session =>
      new Date(session.lesson.startDateTime).getFullYear(),
    ),
  )
  const oldestYear = [...uniq].sort()[0]
  const select = []

  for (let i = CURRENT_YEAR; i >= oldestYear; i--) {
    select.push(i)
  }
  return select
}

export function filterByYear(sessions, year) {
  const yearNumber = Number(year)

  return sessions
    .filter(
      session =>
        new Date(session.lesson.startDateTime).getFullYear() === yearNumber,
    )
    .sort(descendingDates)
}

export function getDefaultPastYear(pastSessions, pastYears) {
  if (pastSessions.length > 0) {
    const [mostRecentPastSession] = [...pastSessions].sort(descendingDates)

    return new Date(mostRecentPastSession.lesson.startDateTime).getFullYear()
  }

  return pastYears[0] ?? null
}

export function getTimePeriod(startDateTime, endDateTime, timezone) {
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  const end = DateTime.fromISO(endDateTime).setZone(timezone)

  return `${start.toFormat('h')}:${start.toFormat('mm')} - ${end.toFormat('t')}`
}

export function formatSessionTimeRange(startDateTime, endDateTime, timezone) {
  const start = DateTime.fromISO(startDateTime).setZone(timezone)
  const end = DateTime.fromISO(endDateTime).setZone(timezone)
  const startMeridiem = start.toFormat('a')
  const endMeridiem = end.toFormat('a')

  if (startMeridiem === endMeridiem) {
    return `${start.toFormat('h:mm')}-${end.toFormat('h:mm')} ${endMeridiem}`
  }

  return `${start.toFormat('h:mm a')}-${end.toFormat('h:mm a')}`
}

export function formatDate(date, isMobile) {
  if (DateTime.fromISO(date) === DateTime.local().startOf('day')) return 'Today'

  return isMobile
    ? DateTime.fromISO(date).toFormat(MOBILE_DATE_FORMAT)
    : DateTime.fromISO(date).toFormat(DATE_FORMAT)
}

export function useSessionDates(startDateTime, endDateTime, timezone) {
  const webDate = formatDate(startDateTime, false)
  const mobileDate = formatDate(startDateTime, true)
  const timePeriod = getTimePeriod(startDateTime, endDateTime, timezone)

  return { webDate, mobileDate, timePeriod }
}

export function formatStudentHeaderMeta(enrollments = []) {
  const parts = []

  const lastLessonRelative = getLastLessonRelative(enrollments)
  if (lastLessonRelative) {
    parts.push(`Last lesson ${lastLessonRelative}`)
  }

  const joinedDate = getJoinedDate(enrollments)
  if (joinedDate) {
    parts.push(`Joined ${joinedDate}`)
  }

  return parts.length ? parts.join(' · ') : null
}

function isCompletedEnrollment(enrollment) {
  if (enrollment.isCanceled) return false
  if (enrollment.lesson?.isCanceled) return false
  if (!enrollment.lesson?.startDateTime) return false

  return DateTime.fromISO(enrollment.lesson.startDateTime) < DateTime.now()
}

export function getLastLessonRelative(enrollments = []) {
  const [mostRecentPastEnrollment] = enrollments
    .filter(isCompletedEnrollment)
    .sort(descendingDates)

  if (!mostRecentPastEnrollment) return null

  const relative = DateTime.fromISO(
    mostRecentPastEnrollment.lesson.startDateTime,
  ).toRelative()

  return relative || null
}

export function getJoinedDate(enrollments = []) {
  const joinedAt = enrollments.reduce((earliest, enrollment) => {
    if (!enrollment.createdAt) return earliest

    const createdAt = DateTime.fromISO(enrollment.createdAt)
    if (!createdAt.isValid) return earliest

    return !earliest || createdAt < earliest ? createdAt : earliest
  }, null)

  return joinedAt ? joinedAt.toFormat('MMM d, yyyy') : null
}

export { CURRENT_YEAR }
